import UserManager from 'oidc-client';
import { ServerInfo } from './ServerInfo';

let instance: LoginManager | null = null;

export function getCWOPA(user: any): string | null {
  let username;

  if (user?.profile?.preferred_username) {
    username = user.profile.preferred_username;
  } else if (user?.userPrincipalName) {
    username = user.userPrincipalName;
  } else if (user?.profile?.userID) {
    username = user.profile.userID;
  } else {
    return null;
  }
  if (username.includes('@')) {
    return username.split('@')[0];
  } else {
    return username;
  }
}

export function getUserDisplayName(user: any): string | null {
  if (!user) { return null; }
  if (user.profile?.firstname && user.profile?.lastname) {
    return ((user.profile.firstname) + " " + (user.profile.lastname));
  }
  if (user.profile?.given_name && user.profile?.family_name) {
    return user.profile.given_name + " " + user.profile.family_name;
  }

  return null;
}

export function getUserRole(user: any | null): string | null {

  if (!user) { return null; }
  if (user.profile?.roles) {
    return user.profile.roles.split(",")?.[0].slice(3);//Remove first 3 chars i.e., CN=
  }
  return null;
}

export function getTesterId(user: any | null): number | null {

  if (!user) { return null; }
  if (user.profile?.testerId) {
    return user.profile.testerId
  }
  if (user.profile?.testerID) {
    return user.profile.testerID
  }
  return null;
}

export class LoginManager {
  cwopa: string | null;
  tokens: { [scope: string]: string | undefined };
  userManager: UserManager.UserManager | null;

  constructor() {
    if (instance != null) {
      this.cwopa = instance.cwopa;
      this.userManager = instance.userManager;
      this.tokens = instance.tokens;
      return instance;
    } else {
      this.cwopa = null;
      this.userManager = null;
      this.tokens = {};
      instance = this;
    }
  }

  async getUserManager(): Promise<UserManager.UserManager> {
    if (this.userManager) {
      return this.userManager;
    } else {
      const settings = (await ServerInfo.get()).oidcSettings;
      const manager = new UserManager.UserManager(settings);
      // Do NOT auto-redirect on token expiry — that causes a visible page
      // refresh while the user is idle.  Instead, let the next user-initiated
      // API call detect the expired token (via getAccessToken / refreshIdToken)
      // and redirect at that point.  SiteMinder SSO will re-authenticate
      // seamlessly if the session is still active.
      manager.events.addAccessTokenExpired(() => {
        console.warn('Access token expired — will redirect on next user interaction');
      });
      this.userManager = manager;
      return manager;
    }
  }

  async getCurrentUser(): Promise<UserManager.User | null> {
    const manager = await this.getUserManager();
    const userInSessionStorage = await manager.getUser();
    let cwopa;
    if (userInSessionStorage != null) {

      this.cwopa = getCWOPA(userInSessionStorage);
      return userInSessionStorage;
    }

    // Check for OIDC form_post data in sessionStorage.
    // When response_mode=form_post, the APIM policy (or local setupProxy)
    // reads code & state from the POST body, stores them in sessionStorage,
    // and redirects to the SPA.
    const formPostData = sessionStorage.getItem('oidc_form_post');
    if (formPostData) {
      sessionStorage.removeItem('oidc_form_post');
      try {
        const { code, state } = JSON.parse(formPostData);
        if (code && state) {
          const callbackUrl =
            globalThis.location.origin +
            globalThis.location.pathname +
            '#code=' + encodeURIComponent(code) +
            '&state=' + encodeURIComponent(state);

          const userFromFormPost = await manager.signinRedirectCallback(callbackUrl);
          if (userFromFormPost != null) {
            this.cwopa = getCWOPA(userFromFormPost);
            sessionStorage.removeItem('avirpfg_redirectURL');
            return userFromFormPost;
          }
        }
      } catch (error: any) {
        console.warn('OIDC form_post callback error:', error.message);
        const userAfterFormPost = await manager.getUser();
        if (userAfterFormPost != null) {
          this.cwopa = getCWOPA(userAfterFormPost);
          return userAfterFormPost;
        }
      }
    }

    return null;
  }

  async getUserPhoto(): Promise<any> {

    var token = await this.getAccessToken("https://graph.microsoft.com/user.read.all");
    //alert("In getUserPhoto " + token);
    return new Promise(function (resolve, reject) {
      let xhr = new XMLHttpRequest();
      xhr.open("GET", "https://graph.microsoft.com/v1.0/me/photos/64x64/$value", true);

      xhr.setRequestHeader("authorization", "bearer " + token)
      xhr.responseType = "blob";
      xhr.onload = function () {
        if (this.status >= 200 && this.status < 300) {
          resolve(xhr.response);
        } else {
          reject({
            status: this.status,
            statusText: xhr.statusText
          });
        }
      };
      xhr.onerror = function () {
        reject({
          status: this.status,
          statusText: xhr.statusText
        });
      };
      xhr.send();
    });
  }

  async getAccessToken(scope: string): Promise<string> {
    const manager = await this.getUserManager();

    const cachedAccessToken = this.tokens[scope];
    if (cachedAccessToken !== undefined) {
      return cachedAccessToken;
    }
    try {
      let user = await manager.signinSilent({ scope: scope });
      this.tokens[scope] = user.id_token;
      return user.id_token;
    } catch (error) {
      // Refresh failed — redirect to login instead of signing out.
      // SiteMinder SSO will re-authenticate seamlessly if session is active.
      console.warn('Silent renew failed, redirecting to login:', error);
      await this.signin();
      throw new Error('Session expired, redirecting to login');
    }
  }

  async refreshIdToken(scope: string): Promise<void> {
    delete this.tokens[scope];
    const manager = await this.getUserManager();
    try {
      const user = await manager.signinSilent({ scope: scope });
      this.tokens[scope] = user.id_token;
    } catch (error) {
      console.warn('Silent renew failed, redirecting to login:', error);
      await this.signin();
      throw new Error('Session expired, redirecting to login');
    }
  }

  async signin(): Promise<void> {
    const manager = await this.getUserManager();
    // Store the current path so the OIDC callback can redirect back here
    sessionStorage.setItem('oidc_return_path', globalThis.location.pathname);
    await manager.signinRedirect();
  }

  async signout(): Promise<void> {
    const manager = await this.getUserManager();
    const settings = (await ServerInfo.get()).oidcSettings;
    let key = JSON.parse(sessionStorage['oidc.user:' + settings.authority + ':' + settings.client_id]);
    const headers = new Headers();
    headers.append('Content-Type', 'application/x-www-form-urlencoded');
    const formData = new FormData();
    formData.append('token', key['access_token']);
    formData.append('token_type_hint', 'access_token');
    fetch(settings.revocation_endpoint + '?token=' + key['refresh_token'] + "&token_type_hint=refresh_token&client_id=" + settings.client_id + '&client_secret=' , {
      method: 'post',
      headers: headers,
      body: formData
    }).then((response) => {

    }).then(() => {
      this.tokens = {};
      manager.clearStaleState();
      manager.removeUser();
    });
  }
}
